import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LandingArtefactBlobCanvas from './LandingArtefactBlobCanvas';
import { mergeLandingGifCaptureConfig } from './blob/blobVariantConfig';
import {
  DEFAULT_LANDING_BLOB_GRADIENT,
  getLandingBlobRouteKey,
  hasLandingBlobColoredRevealed,
  landingBlobGifPublicUrl,
  markLandingBlobColoredRevealed,
  probeImageUrl,
} from '../utils/landingBlobGifs';
import {
  beginLandingGifLoad,
  endLandingGifLoad,
  getSessionLandingGifSrc,
  readLandingBlobGif,
  rememberSessionLandingGifSrc,
  writeLandingBlobGif,
} from '../utils/landingBlobGifStorage';
import { dataUrlToCanvas, encodeGifFromCanvases, waitAnimationFrames } from '../utils/gifEncode';

/** Minimum time the GIF placeholder stays visible before crossfading to coloured WebGL. */
const GIF_MIN_VISIBLE_MS = 450;
const CAPTURE_FRAME_COUNT = 40;
const CAPTURE_FRAME_DELAY_MS = 50;
const CAPTURE_SETTLE_TIMEOUT_MS = 5000;

function resolveInitialGifState(routeKey, skipGifPhase) {
  if (skipGifPhase) {
    return { src: null, loadState: 'found', captureSettled: true };
  }
  const cached = getSessionLandingGifSrc(routeKey);
  if (cached) {
    return { src: cached, loadState: 'found', captureSettled: true };
  }
  return { src: null, loadState: 'pending', captureSettled: false };
}

/**
 * Landing artefact tile: show stored/default GIF first, then crossfade to live WebGL when ready.
 * Captures grey GIFs from the visible canvas (no extra offscreen WebGL context).
 */
export default function LandingArtefactBlobTile({
  artefactName,
  personas,
  blobProps,
  showWebGL = true,
  onBlobReady,
}) {
  const routeKey = getLandingBlobRouteKey(artefactName, personas);
  const skipGifPhase = hasLandingBlobColoredRevealed(artefactName);
  const initialGif = useMemo(
    () => resolveInitialGifState(routeKey, skipGifPhase),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per artefact mount
    [artefactName],
  );

  const [gifSrc, setGifSrc] = useState(initialGif.src);
  /** pending | found | none */
  const [gifLoadState, setGifLoadState] = useState(initialGif.loadState);
  const [captureSettled, setCaptureSettled] = useState(initialGif.captureSettled);
  const [showColoredWebgl, setShowColoredWebgl] = useState(() => skipGifPhase);
  const [webglFallback, setWebglFallback] = useState(false);
  const [webglReady, setWebglReady] = useState(false);
  const webglReadyRef = useRef(false);
  const gifShownAtRef = useRef(initialGif.src ? Date.now() : 0);
  const revealTimerRef = useRef(null);
  const coloredRevealedRef = useRef(skipGifPhase);
  const captureRef = useRef(null);
  const captureStartedRef = useRef(false);
  const [captureFnReady, setCaptureFnReady] = useState(false);
  const routeKeyRef = useRef(routeKey);
  const gifSrcRef = useRef(initialGif.src);

  gifSrcRef.current = gifSrc;

  const greyBlobProps = useMemo(
    () => ({
      ...blobProps,
      gradient: DEFAULT_LANDING_BLOB_GRADIENT,
      config: mergeLandingGifCaptureConfig(blobProps.config),
    }),
    [blobProps],
  );

  const clearRevealTimer = useCallback(() => {
    if (revealTimerRef.current) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const assignGifSrc = useCallback((requestRouteKey, src) => {
    if (!src || coloredRevealedRef.current || hasLandingBlobColoredRevealed(artefactName)) return false;
    if (gifSrcRef.current === src) return true;
    const remembered = rememberSessionLandingGifSrc(requestRouteKey, src);
    gifShownAtRef.current = Date.now();
    gifSrcRef.current = remembered;
    setGifSrc(remembered);
    setGifLoadState('found');
    setCaptureSettled(true);
    setWebglFallback(false);
    return true;
  }, [artefactName]);

  const loadGif = useCallback(async () => {
    if (coloredRevealedRef.current || hasLandingBlobColoredRevealed(artefactName)) return;
    if (gifSrcRef.current) return;

    const requestRouteKey = routeKey;
    const cached = getSessionLandingGifSrc(requestRouteKey);
    if (cached) {
      assignGifSrc(requestRouteKey, cached);
      return;
    }
    if (!beginLandingGifLoad(requestRouteKey)) return;

    try {
      const fromDb = await readLandingBlobGif(requestRouteKey);
      if (
        requestRouteKey !== routeKeyRef.current ||
        coloredRevealedRef.current ||
        hasLandingBlobColoredRevealed(artefactName) ||
        gifSrcRef.current
      ) {
        return;
      }
      if (fromDb) {
        assignGifSrc(requestRouteKey, URL.createObjectURL(fromDb));
        return;
      }

      const publicUrl = landingBlobGifPublicUrl(requestRouteKey);
      if (await probeImageUrl(publicUrl)) {
        if (
          requestRouteKey !== routeKeyRef.current ||
          coloredRevealedRef.current ||
          hasLandingBlobColoredRevealed(artefactName) ||
          gifSrcRef.current
        ) {
          return;
        }
        assignGifSrc(requestRouteKey, publicUrl);
        return;
      }

      if (requestRouteKey !== routeKeyRef.current || gifSrcRef.current) return;
      setGifLoadState('none');
      setCaptureSettled(false);
    } finally {
      endLandingGifLoad(requestRouteKey);
    }
  }, [routeKey, assignGifSrc, artefactName]);

  const loadGifRef = useRef(loadGif);
  loadGifRef.current = loadGif;

  const revealColoredWebgl = useCallback(() => {
    if (coloredRevealedRef.current) return;
    coloredRevealedRef.current = true;
    markLandingBlobColoredRevealed(artefactName);
    setShowColoredWebgl(true);
    onBlobReady?.(artefactName);
  }, [artefactName, onBlobReady]);

  const scheduleColoredReveal = useCallback(() => {
    if (!webglReadyRef.current || coloredRevealedRef.current || webglFallback) return;
    if (hasLandingBlobColoredRevealed(artefactName)) {
      revealColoredWebgl();
      return;
    }
    if (gifLoadState === 'pending') return;
    if (gifLoadState === 'none' && !captureSettled) return;

    clearRevealTimer();

    if (gifLoadState === 'none') {
      revealColoredWebgl();
      return;
    }

    const delay = Math.max(
      0,
      GIF_MIN_VISIBLE_MS - (Date.now() - (gifShownAtRef.current || Date.now())),
    );
    revealTimerRef.current = window.setTimeout(() => {
      revealColoredWebgl();
    }, delay);
  }, [
    gifLoadState,
    captureSettled,
    webglFallback,
    artefactName,
    clearRevealTimer,
    revealColoredWebgl,
  ]);

  const captureGreyGif = useCallback(async () => {
    if (captureStartedRef.current || !captureRef.current || gifLoadState !== 'none') return;
    if (gifSrcRef.current) return;
    captureStartedRef.current = true;
    try {
      const canvases = [];
      for (let f = 0; f < CAPTURE_FRAME_COUNT; f += 1) {
        await waitAnimationFrames(2);
        const dataUrl = captureRef.current?.();
        if (!dataUrl) break;
        canvases.push(await dataUrlToCanvas(dataUrl));
      }
      if (canvases.length >= 4) {
        const bytes = encodeGifFromCanvases(canvases, CAPTURE_FRAME_DELAY_MS);
        const blob = new Blob([bytes], { type: 'image/gif' });
        await writeLandingBlobGif(routeKey, blob);
        if (!gifSrcRef.current && !coloredRevealedRef.current) {
          const url = getSessionLandingGifSrc(routeKey) || URL.createObjectURL(blob);
          assignGifSrc(routeKey, url);
        }
        return;
      }
    } catch (err) {
      console.warn('[landing-gif]', 'Tile capture failed', artefactName, err);
    } finally {
      setCaptureSettled(true);
    }
  }, [gifLoadState, routeKey, artefactName, assignGifSrc]);

  const handleCaptureReady = useCallback((fn) => {
    captureRef.current = fn;
    setCaptureFnReady(true);
  }, []);

  const handleContextLost = useCallback(() => {
    if (!gifSrcRef.current) return;
    clearRevealTimer();
    coloredRevealedRef.current = false;
    setShowColoredWebgl(false);
    setWebglFallback(true);
    gifShownAtRef.current = Date.now();
  }, [clearRevealTimer]);

  useEffect(() => {
    const alreadyRevealed = hasLandingBlobColoredRevealed(artefactName);
    clearRevealTimer();
    setWebglFallback(false);
    setWebglReady(false);
    webglReadyRef.current = false;
    captureStartedRef.current = false;
    setCaptureFnReady(false);
    captureRef.current = null;
    routeKeyRef.current = routeKey;

    if (alreadyRevealed) {
      gifSrcRef.current = null;
      setGifSrc(null);
      setGifLoadState('found');
      setCaptureSettled(true);
      setShowColoredWebgl(true);
      coloredRevealedRef.current = true;
      gifShownAtRef.current = 0;
      return;
    }

    const cached = getSessionLandingGifSrc(routeKey);
    if (cached) {
      gifSrcRef.current = cached;
      setGifSrc(cached);
      setGifLoadState('found');
      setCaptureSettled(true);
      setShowColoredWebgl(false);
      coloredRevealedRef.current = false;
      gifShownAtRef.current = Date.now();
      return;
    }

    gifSrcRef.current = null;
    setGifSrc(null);
    setGifLoadState('pending');
    setCaptureSettled(false);
    setShowColoredWebgl(false);
    coloredRevealedRef.current = false;
    gifShownAtRef.current = 0;
    void loadGifRef.current();
  }, [artefactName, clearRevealTimer]);

  useEffect(() => {
    routeKeyRef.current = routeKey;
    if (coloredRevealedRef.current || hasLandingBlobColoredRevealed(artefactName)) return;
    if (gifSrcRef.current) return;
    const cached = getSessionLandingGifSrc(routeKey);
    if (cached) {
      assignGifSrc(routeKey, cached);
      return;
    }
    if (gifLoadState !== 'pending') return;
    void loadGifRef.current();
  }, [routeKey, artefactName, gifLoadState, assignGifSrc]);

  useEffect(() => {
    if (gifLoadState !== 'none' || captureSettled) return undefined;
    const timeoutId = window.setTimeout(() => setCaptureSettled(true), CAPTURE_SETTLE_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [gifLoadState, captureSettled]);

  useEffect(() => {
    if (
      gifLoadState !== 'none' ||
      !webglReady ||
      !captureFnReady ||
      captureStartedRef.current ||
      gifSrcRef.current
    ) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      void captureGreyGif();
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [gifLoadState, webglReady, captureFnReady, captureGreyGif]);

  useEffect(() => {
    scheduleColoredReveal();
  }, [gifLoadState, captureSettled, webglFallback, scheduleColoredReveal]);

  useEffect(() => () => clearRevealTimer(), [clearRevealTimer]);

  const handleWebglReady = useCallback(() => {
    webglReadyRef.current = true;
    setWebglReady(true);
    scheduleColoredReveal();
  }, [scheduleColoredReveal]);

  const displayBlobProps = showColoredWebgl ? blobProps : greyBlobProps;
  const enableCapture = gifLoadState === 'none' && !captureStartedRef.current && !gifSrcRef.current;
  const showGifLayer =
    Boolean(gifSrc) &&
    (webglFallback || (!hasLandingBlobColoredRevealed(artefactName) && !showColoredWebgl));
  const awaitingGif = gifLoadState === 'pending' && !gifSrc;

  return (
    <div
      className={`overview-blob-canvas overview-blob-canvas--layered${awaitingGif ? ' overview-blob-canvas--awaiting-gif' : ''}${showGifLayer ? ' overview-blob-canvas--gif-visible' : ''}${showColoredWebgl && !webglFallback ? ' overview-blob-canvas--webgl-ready' : ''}${webglFallback ? ' overview-blob-canvas--webgl-fallback' : ''}`}>
      {gifSrc ? (
        <img
          src={gifSrc}
          className='overview-blob-gif'
          alt=''
          draggable={false}
          decoding='async'
        />
      ) : null}
      {showWebGL ? (
        <div className='overview-blob-canvas__webgl'>
          <LandingArtefactBlobCanvas
            {...displayBlobProps}
            onCaptureReady={enableCapture ? handleCaptureReady : undefined}
            onContextLost={gifSrc ? handleContextLost : undefined}
            onReady={handleWebglReady}
          />
        </div>
      ) : null}
    </div>
  );
}
